/* eslint-disable no-nested-ternary */

import SmallLogoFooter from '@gameday/common/components/form/Common/SmallLogoFooter'
import Tooltip from '@material-ui/core/Tooltip'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'

import * as validations from '../../helpers/validations'
import theme from '../../utils/theme'
import { passwordRegex } from '../../utils/validation'
import { CornerHexagonBg } from '../Background/CornerHexagons'
import { PrimaryButton } from '../Button'
import InputField from '../InputField'
import Paper from '../Paper'
import PasswordCheck from '../PasswordCheck'
import STGTermsAndConditions from '../STGTermsAndConditions'
import H2 from '../Typography/H2'

const initPasswords = {
  password: {
    value: '',
    isError: false,
    errorMessage: 'Please enter password.',
  },
  confirmPassword: {
    value: '',
    isError: false,
    errorMessage: 'Please enter confirm password.',
  },
}
const initialState = {
  showPassword: false,
  showConfirmPassword: false,
  charLength: false,
  capitalLetter: false,
  numberEntered: false,
  specialChar: false,
  passwordsMatch: false,
}

const StyledPageWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: ${props => (props.isAdminPortalVersion ? '100vh' : '100%')};
  ${props => !props.isAdminPortalVersion && 'flex-direction: column'};
  justify-content: center;
  width: 100%;

  .MuiPaper-root {
    padding: 42px 34px;
    ${theme.breakpoints.down(400)} {
      padding: 30px 20px;
    }
  }
  ${props =>
    !props?.isAdminPortalVersion &&
    css`
      > div:first-of-type {
        min-height: 0;
        padding: 60px 30px 30px 30px;
      }
    `}
`
const StyledFormContent = styled.div`
  text-align: center;
  width: 100%;
  a {
    color: ${theme.palette.primary.main};
    font-size: ${theme.typography.fontSizeSm};
    font-weight: ${theme.typography.fontWeightBold};
    display: block;
    margin: 1em 0;
    text-decoration: none;
    text-align: center;
    &:hover {
      text-decoration: underline;
    }
  }
  img {
    max-width: 100%;
  }

  input:-webkit-autofill {
    animation-name: onAutoFillStart;
    transition: background-color 50000s ease-in-out 0s;
  }

  @keyframes onAutoFillStart {
  }
`
const StyledInfo = styled.div`
  color: var(--gameday-body-textColor, ${theme.body.textColor});
`
const StyledSuccess = styled.div`
  color: ${theme.colors.green};
`
const StyledError = styled.div`
  color: ${theme.colors.red};
`

const StyledHeader = styled.div`
  margin: 2em 0;
  p {
    color: var(--gameday-body-textColor, ${theme.body.textColor});
    font-size: ${theme.typography.fontSizeSm};
  }
`

function ResetPasswordPage(props) {
  const {
    formInfo,
    formSuccess,
    formError,
    loadingUpdatePassword,
    title,
    backgroundImage,
    redirectLoginPath,
    authorised,
    showTermsAndConditions,
    logo,
    application,
    customWidth,
    handleSubmit,
    email,
    isAdminPortalVersion,
    primaryColor,
    secondaryColor,
  } = props
  const [state, setState] = useState(initialState)
  const [passwords, setPasswords] = useState(initPasswords)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false)
  const [validation, setValidation] = useState({
    charLength: false,
    capitalLetter: false,
    numberEntered: false,
    specialChar: false,
    passwordValid: false,
    passwordsMatch: false,
  })
  const [emailState, setEmailState] = useState({})
  const [isTooltipOpen, setTooltipOpen] = useState(false)

  useEffect(() => {
    setEmailState(email)
  }, [email])

  const passwordValue = passwords.password.value
  const confirmPasswordValue = passwords.confirmPassword.value
  const { passwordValid, passwordsMatch } = validation
  const enableButton = passwordValid && passwordsMatch

  useEffect(() => {
    !passwordTouched && passwords.password.value && setConfirmPasswordTouched(true)
    !confirmPasswordTouched && passwords.confirmPassword.value && setConfirmPasswordTouched(true)
    setValidation({
      ...validation,
      ...validations.validatePassword(passwordValue),
      passwordsMatch: confirmPasswordValue.length > 0 && passwordValue === confirmPasswordValue,
    })
  }, [passwords])

  const onSubmit = e => {
    e.preventDefault()
    handleSubmit({
      ...passwords,
      email: {
        value: emailState,
        isError: false,
      },
    })
  }

  /* eslint-disable prefer-destructuring */
  const onChange = (event, name) => {
    const value = event.target.value
    const temp = { ...passwords }
    temp[name] = { ...temp[name] }
    temp[name].value = value
    setPasswords(temp)
  }

  const handleAutoFill = e => {
    if (e.animationName === 'onAutoFillStart') {
      const temp = { ...passwords }
      setPasswords(temp)
    }
  }

  /* eslint-disable no-shadow */
  const toggleShowPassword = () =>
    setState(state => ({
      ...state,
      showPassword: !state.showPassword,
    }))

  const toggleShowConfirmPassword = () =>
    setState(state => ({
      ...state,
      showConfirmPassword: !state.showConfirmPassword,
    }))
  /* eslint-enable no-shadow */

  const toggleTooltip = tooltip => setTooltipOpen(tooltip)

  return (
    <StyledPageWrap isAdminPortalVersion={isAdminPortalVersion}>
      <Paper backgroundImage={backgroundImage} application={application} width={customWidth}>
        <StyledFormContent>
          <img src={logo} alt="GameDay Logo" />
          {formInfo && <StyledInfo>{formInfo}</StyledInfo>}
          {formSuccess && <StyledSuccess>{formSuccess}</StyledSuccess>}
          {formError && <StyledError>{formError}</StyledError>}
          <StyledHeader>
            <H2>{title}</H2>
            {authorised ? (
              <p>
                Enter a new password for <b>{`${emailState ? decodeURIComponent(emailState) : ''}`}</b>.
              </p>
            ) : (
              <p>Invalid Access</p>
            )}
          </StyledHeader>
          {authorised && (
            <form onSubmit={onSubmit}>
              <Tooltip
                PopperProps={{
                  disablePortal: true,
                }}
                placement="top"
                open={isTooltipOpen}
                disableFocusListener
                disableTouchListener
                title={<PasswordCheck password={passwords.password} onChange={newState => setState({ ...newState })} />}
              >
                <InputField
                  id="Password"
                  label="Password"
                  inputProps={{
                    autoComplete: 'new-password',
                    pattern: passwordRegex,
                  }}
                  isRequired
                  errorText={
                    passwordTouched && passwordValue.length > 0 && !passwordValid
                      ? 'Password strength criteria not met.'
                      : passwordValue.length > 100
                        ? 'Password cannot exceed 100 characters'
                        : ''
                  }
                  value={passwords.password.value}
                  onChange={event => onChange(event, 'password')}
                  onBlur={() => {
                    setPasswordTouched(true)
                    toggleTooltip(false)
                  }}
                  onFocus={() => toggleTooltip(true)}
                  type={state.showPassword ? 'text' : 'password'}
                  name="password"
                  withBorder
                  icon={state.showPassword ? <VisibilityOff /> : <Visibility />}
                  handleOnClickIcon={toggleShowPassword}
                  onAnimationStart={handleAutoFill}
                />
              </Tooltip>
              <InputField
                id="Confirm Password"
                label="Confirm Password"
                inputProps={{
                  autoComplete: 'new-password',
                  pattern: passwordRegex,
                }}
                isRequired
                errorText={
                  confirmPasswordTouched && confirmPasswordValue.length > 0 && !passwordsMatch
                    ? 'Password does not match.'
                    : passwordValue.length > 100
                      ? 'Confirm password cannot exceed 100 characters'
                      : ''
                }
                value={passwords.confirmPassword.value}
                onChange={event => onChange(event, 'confirmPassword')}
                onBlur={() => {
                  setConfirmPasswordTouched(true)
                }}
                type={state.showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                withBorder
                icon={state.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                handleOnClickIcon={toggleShowConfirmPassword}
              />
              <PrimaryButton type="submit" disabled={!enableButton} fullWidth style={{ marginTop: '1em' }}>
                {loadingUpdatePassword ? 'Processing...' : 'Submit'}
              </PrimaryButton>
            </form>
          )}
          <Link to={`${redirectLoginPath}`}>Back to Login</Link>
        </StyledFormContent>
        {showTermsAndConditions && <STGTermsAndConditions />}
      </Paper>
      {!isAdminPortalVersion && <SmallLogoFooter />}
      {!backgroundImage && <CornerHexagonBg useWindow primaryColor={primaryColor} secondaryColor={secondaryColor} />}
    </StyledPageWrap>
  )
}

ResetPasswordPage.defaultProps = {
  application: '',
}

export default ResetPasswordPage
